import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  MoreVertical,
  Edit,
  Trash2,
  User,
  Heart,
  Heart as HeartFilled,
  Smile,
  Flag,
  AtSign,
  X,
  Clock,
  Reply
} from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import useReportStore from '../../store/reportStore';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import communityAPI from '../../services/community.api';

const CommentsSection = ({ postId }) => {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [showOptions, setShowOptions] = useState(null);
  
  // Mention states
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentions, setMentions] = useState([]);
  
  const textareaRef = useRef(null);
  const { openReportModal } = useReportStore();
  const commentsEndRef = useRef(null);
  
  const {
    postComments,
    commentsPagination,
    isLoading,
    getPostComments,
    createComment,
    updateComment,
    deleteComment,
    toggleLike
  } = useCommunityStore();
  
  const { user } = useAuthStore();

  useEffect(() => {
    loadComments();
  }, [postId]);

  useEffect(() => {
    scrollToBottom();
  }, [postComments]);

  const loadComments = async () => {
    try {
      await getPostComments(postId, { page: 1, limit: 50 });
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);
    
    const lastChar = value.slice(-1);
    const secondLastChar = value.slice(-2, -1);
    
    if (lastChar === '@' && (value.length === 1 || secondLastChar === ' ' || secondLastChar === '\n')) {
      setShowMentions(true);
      setMentionQuery('');
      setSelectedMentionIndex(0);
    } 
    else if (showMentions && mentionQuery !== null) {
      const lines = value.split('\n');
      const currentLine = lines[lines.length - 1];
      const atIndex = currentLine.lastIndexOf('@');
      
      if (atIndex !== -1) {
        const query = currentLine.substring(atIndex + 1);
        setMentionQuery(query);
        
        if (query.length > 0) {
          searchMentions(query);
        } else {
          setMentionResults([]);
        }
      } else {
        setShowMentions(false);
        setMentionQuery('');
      }
    }
  };

  const searchMentions = async (query) => {
    try {
      const response = await communityAPI.searchUsers({ query, limit: 5 });
      setMentionResults(response.data.users || []);
    } catch (error) {
      console.error('Search mentions error:', error);
      setMentionResults([]);
    }
  };

  const selectMention = (selectedUser) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const text = newComment;
    
    const beforeCursor = text.substring(0, cursorPos);
    const lastAtPos = beforeCursor.lastIndexOf('@');
    
    if (lastAtPos !== -1) {
      const beforeAt = text.substring(0, lastAtPos);
      const afterCursor = text.substring(cursorPos);
      const newText = `${beforeAt}@${selectedUser.firstName} ${selectedUser.lastName} `;
      
      setNewComment(newText + afterCursor);
      setMentions(prev => [...prev, { 
        id: selectedUser.id, 
        name: `${selectedUser.firstName} ${selectedUser.lastName}` 
      }]);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newText.length, newText.length);
      }, 0);
    }
    
    setShowMentions(false);
    setMentionQuery('');
    setMentionResults([]);
  };

  const handleKeyDown = (e) => {
    if (showMentions && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < mentionResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : mentionResults.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectMention(mentionResults[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        setMentionResults([]);
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
      e.preventDefault();
      if (newComment.trim()) {
        handleSubmitComment(e);
      }
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const mentionRegex = /@([A-Za-z\s]+)/g;
      const extractedMentions = [];
      let match;
      
      while ((match = mentionRegex.exec(newComment)) !== null) {
        const name = match[1].trim();
        const mentionedUser = mentionResults.find(u => 
          `${u.firstName} ${u.lastName}`.includes(name) ||
          name.includes(u.firstName) || 
          name.includes(u.lastName)
        );
        
        if (mentionedUser) {
          extractedMentions.push(mentionedUser.id);
        }
      }

      let comment;
      if (editingComment) {
        comment = await updateComment(editingComment.id, newComment);
        setEditingComment(null);
        toast.success('Comment updated!');
      } else {
        comment = await createComment(postId, newComment);
        toast.success('Comment added!');
      }
      
      if (extractedMentions.length > 0 && comment?.id) {
        for (const mentionedUserId of extractedMentions) {
          try {
            await communityAPI.createMentionNotification({
              mentionedUserId,
              commentId: comment.id,
              postId: postId
            });
          } catch (error) {
            console.error('Failed to create mention notification:', error);
          }
        }
      }
      
      setNewComment('');
      setReplyingTo(null);
      setMentions([]);
    } catch (error) {
      console.error('Failed to submit comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await deleteComment(commentId);
      setShowOptions(null);
      toast.success('Comment deleted!');
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await toggleLike(null, commentId);
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Just now';
    }
  };

  const CommentItem = ({ comment }) => {
    const isAuthor = comment.user.id === user?.id;
    const [showMobileOptions, setShowMobileOptions] = useState(false);
    
    return (
      <div
        id={`comment-${comment.id}`}
        className="flex space-x-2 sm:space-x-3 p-3 sm:p-4 hover:bg-gray-50 transition-colors border-b border-primary-100/50 last:border-0"
      >
        <div className="flex-shrink-0">
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center">
            {comment.user.avatar ? (
              <img 
                src={comment.user.avatar} 
                alt={comment.user.firstName}
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
              />
            ) : (
              <span className="text-white text-xs sm:text-sm font-semibold">
                {comment.user.firstName[0]}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
                  {comment.user.firstName} {comment.user.lastName}
                </h4>
                {comment.user.businessTags && comment.user.businessTags.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-primary-50 text-primary-700 text-[9px] sm:text-xs rounded-lg border border-primary-200 whitespace-nowrap">
                    {comment.user.businessTags[0]}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-0.5">
                <p className="text-[10px] sm:text-xs text-gray-500 flex items-center">
                  <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                  {formatDate(comment.createdAt)}
                </p>
                {comment.updatedAt !== comment.createdAt && (
                  <span className="text-[9px] sm:text-xs text-gray-400">• Edited</span>
                )}
              </div>
            </div>
            
            <div className="relative flex-shrink-0">
              <button
                onClick={() => {
                  if (window.innerWidth < 640) {
                    setShowMobileOptions(!showMobileOptions);
                  } else {
                    setShowOptions(showOptions === comment.id ? null : comment.id);
                  }
                }}
                className="p-1 sm:p-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
              >
                <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
              </button>
              
              {/* Mobile Bottom Sheet for Options */}
              {showMobileOptions && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setShowMobileOptions(false)} />
                  <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#E8ECF5] rounded-t-2xl shadow-neomorph overflow-hidden">
                    <div className="p-4 border-b border-primary-100/50">
                      <h4 className="text-center font-semibold text-gray-800">Comment Options</h4>
                    </div>
                    <div className="p-2">
                      {isAuthor ? (
                        <>
                          <button
                            onClick={() => {
                              setEditingComment(comment);
                              setNewComment(comment.content);
                              setShowMobileOptions(false);
                              setShowMentions(false);
                            }}
                            className="flex items-center w-full p-3 text-sm text-gray-700 hover:bg-white rounded-lg transition-all"
                          >
                            <div className="p-1.5 bg-blue-50 rounded-lg mr-3">
                              <Edit className="h-4 w-4 text-blue-600" />
                            </div>
                            Edit Comment
                          </button>
                          <button
                            onClick={() => {
                              handleDeleteComment(comment.id);
                              setShowMobileOptions(false);
                            }}
                            className="flex items-center w-full p-3 text-sm text-red-600 hover:bg-white rounded-lg transition-all"
                          >
                            <div className="p-1.5 bg-red-50 rounded-lg mr-3">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </div>
                            Delete Comment
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setReplyingTo(comment);
                              setShowMobileOptions(false);
                            }}
                            className="flex items-center w-full p-3 text-sm text-gray-700 hover:bg-white rounded-lg transition-all"
                          >
                            <div className="p-1.5 bg-primary-50 rounded-lg mr-3">
                              <Reply className="h-4 w-4 text-primary-600" />
                            </div>
                            Reply
                          </button>
                          <button
                            onClick={() => {
                              openReportModal({
                                type: 'comment',
                                id: comment.id,
                                data: {
                                  content: comment.content,
                                  author: comment.user
                                }
                              });
                              setShowMobileOptions(false);
                            }}
                            className="flex items-center w-full p-3 text-sm text-gray-700 hover:bg-white rounded-lg transition-all"
                          >
                            <div className="p-1.5 bg-yellow-50 rounded-lg mr-3">
                              <Flag className="h-4 w-4 text-yellow-600" />
                            </div>
                            Report Comment
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
              
              {/* Desktop Dropdown */}
              {!showMobileOptions && showOptions === comment.id && (
                <div className="absolute right-0 mt-2 w-48 bg-[#E8ECF5] rounded-xl shadow-neomorph border border-primary-100/30 z-10 overflow-hidden">
                  {isAuthor ? (
                    <>
                      <button
                        onClick={() => {
                          setEditingComment(comment);
                          setNewComment(comment.content);
                          setShowOptions(null);
                          setShowMentions(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-white transition-all"
                      >
                        <div className="p-1.5 bg-blue-50 rounded-lg mr-3">
                          <Edit className="h-4 w-4 text-blue-600" />
                        </div>
                        Edit Comment
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-white transition-all"
                      >
                        <div className="p-1.5 bg-red-50 rounded-lg mr-3">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </div>
                        Delete Comment
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setReplyingTo(comment);
                          setShowOptions(null);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-white transition-all"
                      >
                        <div className="p-1.5 bg-primary-50 rounded-lg mr-3">
                          <Reply className="h-4 w-4 text-primary-600" />
                        </div>
                        Reply
                      </button>
                      <button
                        onClick={() => {
                          openReportModal({
                            type: 'comment',
                            id: comment.id,
                            data: {
                              content: comment.content,
                              author: comment.user
                            }
                          });
                          setShowOptions(null);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-white transition-all"
                      >
                        <div className="p-1.5 bg-yellow-50 rounded-lg mr-3">
                          <Flag className="h-4 w-4 text-yellow-600" />
                        </div>
                        Report Comment
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <p className="mt-1.5 sm:mt-2 text-gray-700 whitespace-pre-line text-xs sm:text-sm break-words">
            {replyingTo?.id === comment.id && (
              <span className="text-primary-600 font-medium">@{comment.user.firstName} </span>
            )}
            {comment.content}
          </p>
          
          <div className="mt-2 flex items-center space-x-3 sm:space-x-4">
            <button
              onClick={() => handleLikeComment(comment.id)}
              className="flex items-center space-x-1 px-2 py-1 rounded-lg hover:bg-gray-50 transition-all group"
            >
              {comment.likedByUser ? (
                <HeartFilled className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 fill-current" />
              ) : (
                <Heart className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-red-500" />
              )}
              <span className={`text-[10px] sm:text-xs font-medium ${comment.likedByUser ? 'text-red-500' : 'text-gray-500'}`}>
                {comment._count?.likes || 0}
              </span>
            </button>
            
            <button
              onClick={() => {
                setReplyingTo(comment);
                document.querySelector('textarea')?.focus();
              }}
              className="flex items-center space-x-1 px-2 py-1 rounded-lg hover:bg-gray-50 transition-all text-[10px] sm:text-xs text-gray-500 hover:text-primary-600"
            >
              <Reply className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
              Reply
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border-t border-primary-100/50">
      {/* Comments Header */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-primary-100/50">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-800 text-sm flex items-center">
            <div className="p-1 sm:p-1.5 bg-primary-50 rounded-lg mr-1.5 sm:mr-2">
              <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600" />
            </div>
            Comments {postComments.length > 0 && `(${postComments.length})`}
          </h3>
          
          {commentsPagination?.totalPages > commentsPagination?.page && (
            <button
              onClick={() => getPostComments(postId, { 
                page: commentsPagination.page + 1 
              })}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white text-xs sm:text-sm text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all"
              disabled={isLoading}
            >
              Load more
            </button>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading && postComments.length === 0 ? (
          <div className="space-y-2 sm:space-y-3 p-3 sm:p-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex space-x-2 sm:space-x-3">
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse"></div>
                <div className="flex-1 space-y-1.5 sm:space-y-2">
                  <div className="h-3 sm:h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                  <div className="h-2 sm:h-3 bg-gray-200 rounded animate-pulse w-32"></div>
                </div>
              </div>
            ))}
          </div>
        ) : postComments.length > 0 ? (
          <div>
            {postComments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
            <div ref={commentsEndRef} />
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-50 shadow-neomorph-inset mb-3 sm:mb-4">
              <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
            </div>
            <p className="text-xs sm:text-sm text-gray-500">No comments yet. Be the first to comment!</p>
          </div>
        )}
      </div>

      {/* Add Comment Form */}
      <div className="p-3 sm:p-4 border-t border-primary-100/50">
        <form onSubmit={handleSubmitComment}>
          {replyingTo && (
            <div className="mb-2 sm:mb-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-50 rounded-lg border border-primary-200 flex items-center justify-between">
              <span className="text-xs sm:text-sm text-primary-700 flex items-center">
                <Reply className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                Replying to <strong className="ml-0.5 sm:ml-1">{replyingTo.user.firstName}</strong>
              </span>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                className="p-0.5 sm:p-1 hover:bg-primary-100 rounded-lg transition-all"
              >
                <X className="h-3 w-3 sm:h-4 sm:w-4 text-primary-600" />
              </button>
            </div>
          )}
          
          {editingComment && (
            <div className="mb-2 sm:mb-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-yellow-50 rounded-lg border border-yellow-200 flex items-center justify-between">
              <span className="text-xs sm:text-sm text-yellow-700">
                Editing comment
              </span>
              <button
                type="button"
                onClick={() => {
                  setEditingComment(null);
                  setNewComment('');
                  setShowMentions(false);
                }}
                className="p-0.5 sm:p-1 hover:bg-yellow-100 rounded-lg transition-all"
              >
                <X className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-600" />
              </button>
            </div>
          )}
          
          <div className="flex space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center">
                {user?.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.firstName}
                    className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
                  />
                ) : (
                  <span className="text-white font-semibold text-xs sm:text-sm">
                    {user?.firstName?.[0]}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={newComment}
                  onChange={handleCommentChange}
                  onKeyDown={handleKeyDown}
                  placeholder={editingComment ? "Edit your comment..." : "Add a comment... Use @ to mention someone"}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none min-h-[70px] sm:min-h-[80px] pr-16 sm:pr-20 text-sm"
                  rows="2"
                  maxLength={500}
                />
                
                {/* Mentions Dropdown */}
                {showMentions && (
                  <div className="absolute z-20 bottom-full mb-1 w-full bg-[#E8ECF5] rounded-xl shadow-neomorph border border-primary-100/30 max-h-48 sm:max-h-60 overflow-y-auto">
                    {mentionResults.length > 0 ? (
                      mentionResults.map((user, index) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectMention(user)}
                          className={`flex items-center w-full px-3 sm:px-4 py-2 sm:py-3 text-left hover:bg-white transition-all ${
                            index === selectedMentionIndex ? 'bg-white/50' : ''
                          }`}
                        >
                          <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center mr-2 sm:mr-3">
                            {user.avatar ? (
                              <img 
                                src={user.avatar} 
                                alt={user.firstName}
                                className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg object-cover"
                              />
                            ) : (
                              <span className="text-white text-[10px] sm:text-xs font-semibold">
                                {user.firstName[0]}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 text-xs sm:text-sm truncate">
                              {user.firstName} {user.lastName}
                            </div>
                            {user.businessTags && user.businessTags.length > 0 && (
                              <div className="text-[9px] sm:text-xs text-gray-500 truncate">
                                {user.businessTags[0]}
                              </div>
                            )}
                          </div>
                        </button>
                      ))
                    ) : mentionQuery.length > 0 ? (
                      <div className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 text-xs sm:text-sm">
                        No users found for "{mentionQuery}"
                      </div>
                    ) : (
                      <div className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 text-xs sm:text-sm">
                        Start typing to search for users...
                      </div>
                    )}
                  </div>
                )}
                
                <div className="absolute right-2 bottom-2 flex items-center space-x-1 sm:space-x-2">
                  {mentions.length > 0 && (
                    <div className="flex items-center text-[10px] sm:text-xs text-primary-600 bg-primary-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                      <AtSign className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      <span className="hidden sm:inline">{mentions.length}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-all"
                    title="Add emoji"
                  >
                    <Smile className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                  <span className="text-[9px] sm:text-xs text-gray-500 bg-gray-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                    {newComment.length}/500
                  </span>
                </div>
              </div>
              
              <div className="mt-2 flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="text-[9px] sm:text-xs text-gray-500 hidden sm:block">
                  <span className="px-2 py-1 bg-gray-50 rounded-lg">
                    Press Enter to submit, Shift+Enter for new line
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={!newComment.trim() || isLoading}
                  className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm flex items-center justify-center disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  {editingComment ? 'Update' : 'Post Comment'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommentsSection;